/** Reseller list page with hierarchy visualization and reseller-specific actions. */
import React, { useMemo, useEffect, useState, useCallback } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Tooltip,
  Paper,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  FormControl,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Edit as EditIcon,
  AccountBalanceWallet as TopUpIcon,
  BarChart as PerformanceIcon,
  PowerSettingsNew as PowerIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  Visibility as ViewIcon,
  ManageAccounts as PortalAccessIcon,
  DeleteOutline as RevokeIcon,
  ContentCopy as CopyIcon,
} from "@mui/icons-material";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  createColumnHelper,
  SortingState,
  ColumnFiltersState,
  flexRender,
} from "@tanstack/react-table";
import apiClient, { resellersApi } from "../../api/client";
import ResellerHierarchyTree from "../../components/accounts/ResellerHierarchyTree";

/** API base URL. */
const API_BASE_URL = process.env.REACT_APP_API_URL || "";

/** Reseller interface matching backend schema. */
export interface Reseller {
  id: string;
  tenant: string;
  account: string;
  account_type: string;
  balance: number;
  credit_limit: number | null;
  disabled: boolean;
  level: number | null;
  margin_percent: number | null;
  margin_floor: number | null;
  parent_id?: string | null;
  full_account_path?: string;
  created_at?: string;
  updated_at?: string;
}

/** Column helper for TanStack Table. */
const columnHelper = createColumnHelper<Reseller>();

/** Reseller level chip component. */
function LevelChip({ level }: { level: number | null }) {
  const getLevelColor = (lvl: number | null) => {
    if (lvl === null) return "default";
    if (lvl === 1) return "primary";
    if (lvl === 2) return "secondary";
    if (lvl === 3) return "info";
    return "default";
  };

  const getLevelLabel = (lvl: number | null) => {
    if (lvl === null) return "Root";
    return `Level ${lvl}`;
  };

  return (
    <Chip
      label={getLevelLabel(level)}
      color={getLevelColor(level)}
      size="small"
    />
  );
}

/** Status chip component. */
function StatusChip({ disabled }: { disabled: boolean }) {
  return (
    <Chip
      label={disabled ? "Suspended" : "Active"}
      color={disabled ? "error" : "success"}
      size="small"
    />
  );
}

/** Reseller list page component. */
export default function ResellerList() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMedium = useMediaQuery(theme.breakpoints.down("md"));
  const [resellers, setResellers] = useState<Reseller[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [selectedReseller, setSelectedReseller] = useState<Reseller | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [hierarchyVisible, setHierarchyVisible] = useState(!isMobile);



  /** Fetch reseller accounts from API. */
  const fetchResellers = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await apiClient.get("/api/v1/resellers/");
      setResellers(response.data.resellers || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to load resellers");
    } finally {
      setLoading(false);
    }
  };

  /** Admin: balance adjust (resellers are accounts). */
  const [topUpReseller, setTopUpReseller] = useState<Reseller | null>(null);
  const [topUpAmount, setTopUpAmount] = useState("");
  const [topUpBusy, setTopUpBusy] = useState(false);

  const handleTopUpSubmit = useCallback(async () => {
    if (!topUpReseller || !topUpAmount) return;
    setTopUpBusy(true);
    try {
      await apiClient.post(
        `/api/v1/accounts/${encodeURIComponent(topUpReseller.id)}/balance`,
        { amount: parseFloat(topUpAmount), reason: "Admin top-up" }
      );
      setTopUpReseller(null);
      setTopUpAmount("");
      await fetchResellers();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to update balance");
    } finally {
      setTopUpBusy(false);
    }
  }, [topUpReseller, topUpAmount, fetchResellers]);

  /** Admin: enable/disable toggle (PUT disabled). */
  const handleToggleDisabled = async (reseller: Reseller) => {
    try {
      await resellersApi.update(reseller.id, { disabled: !reseller.disabled });
      await fetchResellers();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to update reseller");
    }
  };

  /** Portal-access (dashboard login) management state. */
  interface PortalUser {
    id: string;
    username: string;
    email: string | null;
    is_active: boolean;
    created_at: string;
    last_login: string | null;
  }
  interface InvitationCreated {
    id: string;
    email: string;
    username: string;
    expires_at: string;
    invite_url: string | null;
    resent: boolean;
  }
  const [usersReseller, setUsersReseller] = useState<Reseller | null>(null);
  const [portalUsers, setPortalUsers] = useState<PortalUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", username: "" });
  const [lastInvite, setLastInvite] = useState<InvitationCreated | null>(null);
  const [usersBusy, setUsersBusy] = useState(false);

  /** Open the portal-access dialog and load existing users. */
  const openPortalUsers = useCallback((reseller: Reseller) => {
    setUsersReseller(reseller);
    setPortalUsers([]);
    setInviteForm({ email: "", username: "" });
    setLastInvite(null);
    setUsersLoading(true);
    resellersApi
      .listUsers(reseller.id)
      .then((r) => setPortalUsers(r.data || []))
      .catch((err: any) => setError(err.response?.data?.detail || "Failed to load portal users"))
      .finally(() => setUsersLoading(false));
  }, []);

  /** Send an invitation — the reseller sets their own password; the admin
   *  never handles a secret. invite_url is present only when SMTP is off. */
  const handleSendInvite = useCallback(async () => {
    if (!usersReseller) return;
    setUsersBusy(true);
    try {
      const payload: { email: string; username?: string } = {
        email: inviteForm.email.trim(),
      };
      if (inviteForm.username.trim()) payload.username = inviteForm.username.trim();
      const r = await resellersApi.inviteUser(usersReseller.id, payload);
      setLastInvite(r.data);
      setInviteForm({ email: "", username: "" });
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to send invitation");
    } finally {
      setUsersBusy(false);
    }
  }, [usersReseller, inviteForm]);

  /** Revoke a portal user's dashboard access. */
  const handleRevokePortalUser = useCallback(async (userId: string) => {
    if (!usersReseller) return;
    setUsersBusy(true);
    try {
      await resellersApi.removeUser(usersReseller.id, userId);
      const r = await resellersApi.listUsers(usersReseller.id);
      setPortalUsers(r.data || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to revoke portal user");
    } finally {
      setUsersBusy(false);
    }
  }, [usersReseller]);

  /** Performance dialog state. */
  const [perfReseller, setPerfReseller] = useState<Reseller | null>(null);
  const [perfDays, setPerfDays] = useState(30);
  const [perf, setPerf] = useState<any>(null);
  const [perfLoading, setPerfLoading] = useState(false);

  const [profit, setProfit] = useState<any>(null);

  const openPerformance = useCallback((reseller: Reseller, days = perfDays) => {
    setPerfReseller(reseller);
    setPerfDays(days);
    setPerfLoading(true);
    setPerf(null);
    setProfit(null);
    apiClient
      .get(`/api/v1/resellers/${encodeURIComponent(reseller.id)}/performance`, {
        params: {
          start_date: new Date(Date.now() - days * 86400000).toISOString(),
          end_date: new Date().toISOString(),
        },
      })
      .then((r) => setPerf(r.data))
      .catch((err: any) => setError(err.response?.data?.detail || "Failed to load performance"))
      .finally(() => setPerfLoading(false));
    apiClient
      .get(`/api/v1/resellers/${encodeURIComponent(reseller.id)}/profit`, {
        params: { days },
      })
      .then((r) => setProfit(r.data))
      .catch(() => setProfit(null));
  }, [perfDays]);
  useEffect(() => {
    fetchResellers();
  }, []);

  /** Toggle hierarchy visibility on mobile. */
  useEffect(() => {
    setHierarchyVisible(!isMobile);
  }, [isMobile]);

  /** Table columns definition. */
  const columns = useMemo(
    () => [
      columnHelper.accessor("account", {
        header: "Account Name",
        cell: (info) => info.getValue(),
        enableSorting: true,
      }),
      columnHelper.accessor("level", {
        header: "Reseller Level",
        cell: (info) => <LevelChip level={info.getValue()} />,
        enableSorting: true,
      }),
      columnHelper.accessor("parent_id", {
        header: "Parent Reseller",
        cell: (info) => {
          const parentId = info.getValue();
          const parent = resellers.find((r) => r.id === parentId);
          return parent ? parent.account : "-";
        },
        enableSorting: false,
      }),
      columnHelper.accessor("balance", {
        header: "Balance",
        cell: (info) => `$${info.getValue().toFixed(2)}`,
        enableSorting: true,
      }),
      columnHelper.accessor("margin_percent", {
        header: "Margin (%)",
        cell: (info) => {
          const margin = info.getValue();
          return margin !== null && margin !== undefined ? `${margin}%` : "-";
        },
        enableSorting: true,
      }),
      columnHelper.accessor("disabled", {
        header: "Status",
        cell: (info) => <StatusChip disabled={info.getValue()} />,
        enableSorting: true,
      }),
      columnHelper.accessor((row) => row.id, {
        id: "actions",
        header: "Actions",
        cell: (info) => (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            <Tooltip title="View Hierarchy">
              <IconButton
                size="small"
                onClick={() => handleViewHierarchy(resellers[info.row.index])}
                color="info"
               aria-label="View">
                <ViewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit Reseller">
              <IconButton
                size="small"
                onClick={() => handleEdit(resellers[info.row.index])}
                color="primary"
               aria-label="Edit">
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Performance">
              <IconButton
                size="small"
                onClick={() => openPerformance(resellers[info.row.index])}
                color="secondary"
               aria-label="Performance">
                <PerformanceIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Top Up Balance">
              <IconButton
                size="small"
                onClick={() => setTopUpReseller(resellers[info.row.index])}
                color="warning"
               aria-label="Top up balance">
                <TopUpIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Portal Access">
              <IconButton
                size="small"
                onClick={() => openPortalUsers(resellers[info.row.index])}
                color="info"
               aria-label="Portal access">
                <PortalAccessIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={resellers[info.row.index].disabled ? "Enable Reseller" : "Disable Reseller"}>
              <IconButton
                size="small"
                onClick={() => handleToggleDisabled(resellers[info.row.index])}
                color={resellers[info.row.index].disabled ? "success" : "default"}
               aria-label="Toggle enabled">
                <PowerIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Reseller">
              <IconButton
                size="small"
                onClick={() => setDeleteConfirm(info.row.id)}
                color="error"
               aria-label="Delete">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
        enableSorting: false,
      }),
    ],
    [resellers]
  );

  /** Table instance with sorting and filtering. */
  const table = useReactTable({
    data: resellers,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
  });

  /** Handle view hierarchy. */
  const handleViewHierarchy = (reseller: Reseller) => {
    // Reveal the hierarchy panel (hidden by default on mobile) so the
    // operator can locate the reseller in the tree.
    setHierarchyVisible(true);
  };

  /** Handle edit reseller. */
  const handleEdit = (reseller: Reseller) => {
    setSelectedReseller(reseller);
    setFormOpen(true);
  };

  /** Handle delete reseller. */
  const handleDelete = async (id: string) => {
    try {
      await apiClient.delete(`/api/v1/resellers/${id}`);
      await fetchResellers();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to delete reseller");
    }
  };

  /** Handle delete confirmation. */
  const handleDeleteConfirm = () => {
    if (deleteConfirm) {
      handleDelete(deleteConfirm);
      setDeleteConfirm(null);
    }
  };

  /** Handle save reseller (create or update). */
  const handleSave = async (data: any) => {
    try {
      if (selectedReseller) {
        await apiClient.put(`/api/v1/resellers/${selectedReseller.id}`, data);
      } else {
        await apiClient.post("/api/v1/resellers/", data);
      }
      setFormOpen(false);
      setSelectedReseller(null);
      await fetchResellers();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to save reseller");
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs and Page Title */}
      <Box mb={2}>
        <Typography variant="caption" color="text.secondary">
          Home &gt; Customers &gt; Resellers
        </Typography>
        <Typography variant="h4" fontWeight="bold">
          Resellers
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Action Buttons */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2} flexWrap="wrap">
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setSelectedReseller(null);
            setFormOpen(true);
          }}
        >
          Add Reseller
        </Button>
        {isMobile && (
          <Button
            variant="outlined"
            startIcon={<ExpandMoreIcon />}
            onClick={() => setHierarchyVisible(!hierarchyVisible)}
          >
            {hierarchyVisible ? "Hide" : "Show"} Hierarchy
          </Button>
        )}
      </Box>

      {/* Main Content Grid */}
      <Grid container spacing={2}>
        {/* Hierarchy Tree - Left Side */}
        {!isMobile || hierarchyVisible ? (
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Reseller Hierarchy</Typography>
                {isMobile && (
                  <IconButton size="small" onClick={() => setHierarchyVisible(false)} aria-label="Expand more">
                    <ExpandMoreIcon />
                  </IconButton>
                )}
              </Box>
              <ResellerHierarchyTree
                resellers={resellers}
                onResellerClick={(resellerId) => {
                  const reseller = resellers.find((r) => r.id === resellerId);
                  if (reseller) {
                    handleViewHierarchy(reseller);
                  }
                }}
              />
            </Paper>
          </Grid>
        ) : null}

        {/* Reseller Table - Right Side */}
        <Grid item xs={12} md={hierarchyVisible ? 8 : 12}>
          <Paper elevation={2}>
            {resellers.length === 0 ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography variant="body1" color="text.secondary">
                  No resellers found. Click "Add Reseller" to create your first reseller account.
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: 600, overflowX: "auto" }}>
                <Table stickyHeader>
                  <TableHead>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <TableCell
                            key={header.id}
                            sortDirection={
                              header.column.getIsSorted()
                                ? (header.column.getIsSorted() as "asc" | "desc")
                                : false
                            }
                            sx={{ fontWeight: "bold", minWidth: isMobile ? 100 : 150 }}
                          >
                            {header.column.getCanSort() ? (
                              <TableSortLabel
                                active={header.column.getIsSorted() !== false}
                                direction={header.column.getIsSorted() === "asc" ? "asc" : "desc"}
                                onClick={header.column.getToggleSortingHandler()}
                              >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                              </TableSortLabel>
                            ) : (
                              flexRender(header.column.columnDef.header, header.getContext())
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableHead>
                  <TableBody>
                    {table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                        hover
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Add/Edit Reseller Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedReseller ? "Edit Reseller" : "Add Reseller"}</DialogTitle>
        <DialogContent>
          <ResellerForm
            reseller={selectedReseller}
            resellers={resellers}
            onSave={handleSave}
            onCancel={() => setFormOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Performance dialog */}
      <Dialog open={perfReseller !== null} onClose={() => setPerfReseller(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Performance — {perfReseller?.account}
          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
            {[7, 30, 90].map((d) => (
              <Chip
                key={d}
                label={`${d}d`}
                size="small"
                color={perfDays === d ? "primary" : "default"}
                onClick={() => perfReseller && openPerformance(perfReseller, d)}
              />
            ))}
          </Box>
        </DialogTitle>
        <DialogContent>
          {perfLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : perf ? (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {([
                  ["Calls", perf.calls],
                  ["Answered", perf.answered],
                  ["ASR", `${(perf.asr * 100).toFixed(1)}%`],
                  ["Minutes", Math.round((perf.billsec || 0) / 60)],
                  ["Supplier cost", perf.supplier_cost != null ? `$${Number(perf.supplier_cost).toFixed(4)}` : "—"],
                  ["Last call", perf.last_call ? new Date(perf.last_call).toLocaleDateString() : "—"],
                ] as [string, any][]).map(([label, value]) => (
                  <Grid item xs={4} key={label}>
                    <Paper variant="outlined" sx={{ p: 1.5, textAlign: "center" }}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="h6">{String(value)}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              {profit && (
                <Alert
                  severity={profit.totals.revenue > 0 ? "success" : "info"}
                  sx={{ mb: 2 }}
                >
                  <strong>Last {profit.period.days} days:</strong>{" "}
                  {profit.totals.calls} calls · {profit.totals.minutes} min ·
                  revenue ${Number(profit.totals.revenue).toFixed(4)}
                  {profit.supplier_cost != null && (
                    <> · supplier cost ${Number(profit.supplier_cost).toFixed(4)}</>
                  )}
                  {" — revenue = offline-rated CDR costs on this subtree"}
                </Alert>
              )}
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Top destinations
              </Typography>
              {perf.top_destinations?.length ? (
                perf.top_destinations.map((d: any, i: number) => (
                  <Box key={i} sx={{ display: "flex", justifyContent: "space-between", py: 0.5, borderBottom: 1, borderColor: "divider" }}>
                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>{d.destination || "-"}</Typography>
                    <Typography variant="body2">{d.calls} calls · {Math.round(d.billsec / 60)} min</Typography>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">No calls in this period.</Typography>
              )}
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPerfReseller(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Top-up dialog */}
      <Dialog open={topUpReseller !== null} onClose={() => setTopUpReseller(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Top Up — {topUpReseller?.account}</DialogTitle>
        <DialogContent>
          <TextField
            label="Amount (negative to deduct)"
            type="number"
            fullWidth
            value={topUpAmount}
            onChange={(e) => setTopUpAmount(e.target.value)}
            InputProps={{ startAdornment: <Box sx={{ mr: 1 }}>$</Box> }}
            autoFocus
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTopUpReseller(null)}>Cancel</Button>
          <Button
            onClick={handleTopUpSubmit}
            variant="contained"
            disabled={!topUpAmount || topUpBusy}
            startIcon={topUpBusy ? <CircularProgress size={16} /> : <TopUpIcon />}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Portal access (dashboard login) dialog */}
      <Dialog open={usersReseller !== null} onClose={() => setUsersReseller(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Portal Access — {usersReseller?.account}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Invite dashboard logins for this reseller. The recipient sets their own
            password via a single-use link (72h); you never handle their secret.
          </Typography>

          {usersLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Existing portal users */}
              {portalUsers.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No portal users yet. Send an invitation below so this reseller can
                  access their dashboard at <Box component="code" sx={{ fontFamily: "monospace" }}>/reseller/login</Box>.
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>Username</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Last Login</TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {portalUsers.map((u) => (
                        <TableRow key={u.id} hover>
                          <TableCell>{u.username}</TableCell>
                          <TableCell>{u.email || "—"}</TableCell>
                          <TableCell>
                            {u.last_login ? new Date(u.last_login).toLocaleString() : "Never"}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Revoke access">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={usersBusy}
                                  aria-label="Revoke"
                                  onClick={() => handleRevokePortalUser(u.id)}
                                >
                                  <RevokeIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Confirmation for a just-sent invitation */}
              {lastInvite && (
                <Alert
                  severity="success"
                  sx={{ mb: 2 }}
                  onClose={() => setLastInvite(null)}
                >
                  {lastInvite.resent ? "Invitation re-sent" : "Invitation sent"} to{" "}
                  <strong>{lastInvite.email}</strong> (username{" "}
                  <strong>{lastInvite.username}</strong>, expires{" "}
                  {new Date(lastInvite.expires_at).toLocaleString()}).
                  {lastInvite.invite_url && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" display="block" gutterBottom>
                        Email delivery is not configured — relay this single-use link
                        to the reseller:
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <TextField
                          size="small"
                          fullWidth
                          value={lastInvite.invite_url}
                          inputProps={{ readOnly: true }}
                          InputProps={{ sx: { fontFamily: "monospace", fontSize: 12 } }}
                        />
                        <IconButton
                          size="small"
                          aria-label="Copy invite link"
                          onClick={() => navigator.clipboard?.writeText(lastInvite.invite_url || "")}
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  )}
                </Alert>
              )}

              <Divider sx={{ mb: 2 }} />

              {/* Invitation form */}
              <Typography variant="subtitle2" gutterBottom>
                Invite Portal User
              </Typography>
              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={12} sm={7}>
                  <TextField
                    label="Email (required)"
                    type="email"
                    fullWidth
                    size="small"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm((p) => ({ ...p, email: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField
                    label="Username (optional)"
                    fullWidth
                    size="small"
                    value={inviteForm.username}
                    onChange={(e) => setInviteForm((p) => ({ ...p, username: e.target.value }))}
                    helperText="Defaults to the email local-part"
                  />
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUsersReseller(null)}>Close</Button>
          <Button
            onClick={handleSendInvite}
            variant="contained"
            disabled={usersBusy || !inviteForm.email.trim()}
            startIcon={usersBusy ? <CircularProgress size={16} /> : <PortalAccessIcon />}
          >
            Send Invitation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteConfirm !== null} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Confirm Delete Reseller</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this reseller? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/** Reseller form component. */
interface ResellerFormProps {
  reseller: Reseller | null;
  resellers: Reseller[];
  onSave: (data: any) => void;
  onCancel: () => void;
}

function ResellerForm({ reseller, resellers, onSave, onCancel }: ResellerFormProps) {
  const [formData, setFormData] = useState({
    tenant: reseller?.tenant || "b1",
    account: reseller?.account || "",
    parent_id: reseller?.parent_id || "",
    account_type: reseller?.account_type || "reseller",
    balance: reseller?.balance || 0,
    margin_percent: reseller?.margin_percent || 0,
    margin_floor: reseller?.margin_floor || 0,
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  /** Handle form field change. */
  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  /** Validate form. */
  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.account.trim()) {
      newErrors.account = "Account name is required";
    }
    if (formData.balance < 0) {
      newErrors.balance = "Balance must be >= 0";
    }
    if (formData.margin_percent < 0) {
      newErrors.margin_percent = "Margin must be >= 0";
    }
    if (formData.margin_floor < 0) {
      newErrors.margin_floor = "Margin floor must be >= 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /** Handle form submit. */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    onSave({
      ...formData,
      parent_id: formData.parent_id || null,
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Account Name"
            value={formData.account}
            onChange={(e) => handleChange("account", e.target.value)}
            error={!!errors.account}
            helperText={errors.account}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Parent Reseller</InputLabel>
            <Select
              value={formData.parent_id}
              label="Parent Reseller"
              onChange={(e) => handleChange("parent_id", e.target.value)}
            >
              <MenuItem value="">
                <em>None (Level 1 Reseller)</em>
              </MenuItem>
              {resellers.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.account}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Initial Balance"
            type="number"
            value={formData.balance}
            onChange={(e) => handleChange("balance", parseFloat(e.target.value) || 0)}
            error={!!errors.balance}
            helperText={errors.balance}
            InputProps={{
              startAdornment: <Typography>$</Typography>,
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Margin %"
            type="number"
            value={formData.margin_percent}
            onChange={(e) => handleChange("margin_percent", parseFloat(e.target.value) || 0)}
            error={!!errors.margin_percent}
            helperText={errors.margin_percent}
            InputProps={{
              endAdornment: <Typography>%</Typography>,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Margin Floor"
            type="number"
            value={formData.margin_floor}
            onChange={(e) => handleChange("margin_floor", parseFloat(e.target.value) || 0)}
            error={!!errors.margin_floor}
            helperText={errors.margin_floor}
            InputProps={{
              startAdornment: <Typography>$</Typography>,
            }}
          />
        </Grid>
      </Grid>
      <DialogActions sx={{ mt: 3 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="contained">
          {reseller ? "Update" : "Create"} Reseller
        </Button>
      </DialogActions>
    </Box>
  );
}
